import React, { useEffect, useState } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { MessageSquare, Calendar, User, Trash2, CheckCircle, Send, Reply } from 'lucide-react';

export default function FeedbacksPage() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'feedbacks'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFeedbacks(data);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleReply = async (id) => {
    if (!replyText.trim()) return;
    try {
      const docRef = doc(db, 'feedbacks', id);
      await updateDoc(docRef, {
        adminReply: replyText,
        repliedAt: serverTimestamp(),
        status: 'resolved' // Otomatis dianggap selesai jika sudah dibalas
      });
      setReplyText("");
      setSelectedId(null);
    } catch (e) {
      alert("Gagal membalas: " + e.message);
    }
  };

  const deleteFeedback = async (id) => {
    if (window.confirm('Hapus feedback ini selamanya?')) {
      await deleteDoc(doc(db, 'feedbacks', id));
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Memuat feedback...</div>;

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <MessageSquare className="text-blue-400" size={24} />
        <h2 className="text-xl font-bold text-white">Feedback & Masukan Mitra</h2>
      </div>

      <div className="grid gap-6">
        {feedbacks.length === 0 ? (
          <p className="text-gray-500 italic">Belum ada feedback masuk.</p>
        ) : (
          feedbacks.map((item) => (
            <div key={item.id} className={`p-5 rounded-xl border transition ${item.status === 'resolved' ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-800 border-blue-900/30'}`}>
              
              {/* HEADER: INFO USER */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-600/20 p-2 rounded-lg text-blue-400">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      {/* BACA NAMA MITRA (Fallback ke email untuk data lama) */}
                      <h3 className="text-sm font-bold text-gray-100">
                        {item.senderName || item.email || 'Anonymous'}
                      </h3>
                      {/* TAMPILKAN BADGE PERAN (Driver / Terapis / Cleaner) */}
                      {item.role && (
                        <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${
                          item.role === 'Driver' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 
                          item.role === 'Terapis' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 
                          'bg-green-500/20 text-green-400 border border-green-500/30'
                        }`}>
                          {item.role}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-1 uppercase tracking-wider">
                      <Calendar size={12} />
                      {item.createdAt?.toDate().toLocaleString('id-ID')}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedId(selectedId === item.id ? null : item.id)} className="p-2 text-gray-400 hover:text-blue-400 transition" title="Balas">
                    <Reply size={18} />
                  </button>
                  <button onClick={() => deleteFeedback(item.id)} className="p-2 text-gray-400 hover:text-red-400 transition">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* ISI PESAN MITRA */}
              <div className="bg-black/20 p-4 rounded-lg border border-gray-700/50 mb-4">
                {/* Tampilkan Judul jika ada (Dari Driver) */}
                {item.title && <h4 className="text-blue-400 font-bold mb-2 text-sm">{item.title}</h4>}
                <p className="text-gray-300 text-sm leading-relaxed italic">"{item.content}"</p>
              </div>

              {/* TAMPILAN BALASAN ADMIN (JIKA SUDAH ADA) */}
              {item.adminReply && (
                <div className="ml-6 p-4 bg-blue-600/10 border-l-2 border-blue-500 rounded-r-lg mb-4">
                  <div className="text-[10px] font-bold text-blue-400 mb-1 uppercase">Balasan Anda:</div>
                  <p className="text-gray-200 text-sm">{item.adminReply}</p>
                  <div className="text-[9px] text-gray-500 mt-2 italic">
                    Dibalas pada: {item.repliedAt?.toDate().toLocaleString('id-ID')}
                  </div>
                </div>
              )}

              {/* INPUT FORM BALASAN */}
              {selectedId === item.id && (
                <div className="flex gap-2 mt-4 animate-in fade-in duration-300">
                  <input 
                    autoFocus
                    className="flex-1 bg-gray-950 border border-blue-500/50 rounded-lg px-4 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 outline-none"
                    placeholder="Tulis balasan untuk mitra..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <button onClick={() => handleReply(item.id)} className="bg-blue-600 hover:bg-blue-500 text-white p-2 rounded-lg px-4 flex items-center gap-2 text-sm transition font-bold">
                    <Send size={16} /> Kirim
                  </button>
                </div>
              )}

              {item.status === 'resolved' && !selectedId && (
                <div className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-green-500 bg-green-500/10 px-2 py-1 rounded">
                  <CheckCircle size={10} /> Selesai / Dibalas
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}